import { useQuery, useMutation, useInfiniteQuery } from 'react-query'

import { api, useMutationApi } from 'utils/api'

import type { UseQueryOptions } from 'react-query'
import type { LearningGroup, Pagination, Student } from 'types/entities'
import type { AxiosError } from 'axios'

const basePath = '/teachers/v1alpha1/assessment/learning_groups'

interface GetLearningGroupsParams {
  teacher: string
  limit?: number
  offset?: number
}
interface ResponseSuccessGetLearningGroups {
  data: LearningGroup[]
  meta: Pagination
}

export function useAPIGetLearningGroups(params?: GetLearningGroupsParams) {
  return useQuery<ResponseSuccessGetLearningGroups>(
    ['get-learning-groups', params],
    async (): Promise<ResponseSuccessGetLearningGroups> => {
      return api<ResponseSuccessGetLearningGroups>(
        '/teachers/v1alpha1/assessment/learning_groups',
        {
          params,
          validateStatus: (status) => status < 500,
        }
      )
    }
  )
}

export function useAPIGetInfiniteLearningGroups(
  { offset = 0, limit = 20 }: GetLearningGroupsParams = { teacher: '' }
) {
  return useInfiniteQuery<ResponseSuccessGetLearningGroups, Error>(
    ['get-learning-groups-infinite'],
    ({ pageParam = { offset, limit } }) =>
      api<ResponseSuccessGetLearningGroups>(basePath, {
        params: {
          offset: pageParam.offset,
          limit: pageParam.limit,
        },
      }),
    {
      getNextPageParam: ({ meta: { offset, limit, total } }) =>
        offset + limit < total ? { offset: offset + limit, limit } : undefined,
    }
  )
}

export function useAPIGetLearningGroupById(
  id: string,
  opts?: UseQueryOptions<LearningGroup, Error>
) {
  return useQuery<LearningGroup, Error>(
    ['get-learning-groups', id],
    () => api<LearningGroup>(`${basePath}/${id}`),
    {
      enabled: !!id,
      ...opts,
    }
  )
}

export function useAPICreateLearningGroup() {
  return useMutationApi<
    { id: string },
    Error,
    Partial<LearningGroup> & { teacher: string }
  >(basePath, { axiosConfig: { method: 'POST' } })
}

export function useAPIEditLearningGroup(learningGroupId: string) {
  return useMutation(
    (learningGroup: Partial<LearningGroup> & { teacher: string }) =>
      api(`${basePath}/${learningGroupId}`, {
        data: learningGroup,
        method: 'PUT',
      })
  )
}

interface AddStudentToLearningGroupPayload {
  rombelId: string
  studentsId: Partial<Student>[]
}

export function useAPIAddLearningGroupStudents() {
  return useMutation<unknown, AxiosError, AddStudentToLearningGroupPayload>(
    (payload: AddStudentToLearningGroupPayload): Promise<unknown> =>
      api(`${basePath}/students`, { data: payload, method: 'PUT' })
  )
}

export function useAPIRemoveStudentFromLearningGroup() {
  return useMutation((data: { rombelId: string; studentsId: string }) =>
    api(`${basePath}/students/remove`, {
      method: 'PUT',
      data,
    })
  )
}
